import { FileOutlined } from '@ant-design/icons'
import {
  App,
  Flex,
  Result,
  Spin,
} from 'antd/es'
import React, {
  useCallback,
  useEffect,
  useState,
} from 'react'
import useInfiniteScroll from 'react-infinite-scroll-hook'

import { LoadingType } from '@/constants/loading-type'
import {
  getPosts,
  ReadPostsRequest,
} from '@/redux/post-actions'
import {
  createAppSelector,
  useAppDispatch,
  useAppSelector,
} from '@/redux/utils'

import { PostCard } from './post-card'
import s from './post-list.module.css'

const selector = createAppSelector(
  [
    (state, creatorId: number) => state.post.postList[creatorId] || [],
    (state) => state.post.postError,
  ],
  (postList, postError) => {
    return {
      postList,
      postError,
    }
  },
)

export default function PostList({
  creatorId,
  id,
}: {
  creatorId: number
  id: number
}) {
  const dispatch = useAppDispatch()
  const { message } = App.useApp()
  const { postList: list, postError } = useAppSelector((state) =>
    selector(state, creatorId),
  )

  const [loading, setLoading] = useState<LoadingType>(LoadingType.Idle)
  const [hasMore, setHasMore] = useState<boolean>(true)
  const [query, setQuery] = useState<ReadPostsRequest>({
    creatorId,
    limit: 20,
    userId: id,
    offset: 0,
  })

  const loadData = useCallback(
    async (query: ReadPostsRequest) => {
      const loadMore = query.offset > 1
      setLoading(loadMore ? LoadingType.More : LoadingType.Refresh)
      const { hasMore } = await dispatch(getPosts(query)).unwrap()
      setHasMore(hasMore)
      setQuery(query)
      setLoading(LoadingType.Idle)
    }, [dispatch],
  )

  const [sentryRef] = useInfiniteScroll({
    loading: loading !== LoadingType.Idle,
    hasNextPage: hasMore,
    onLoadMore: () => {
      void loadData({ ...query, offset: list.length })
    },
  })

  useEffect(() => {
    if (postError !== undefined) {
      void message.error('Network error.')
    }
  }, [postError, message])

  useEffect(() => {
    if (id !== query.userId || creatorId !== query.creatorId) {
      loadData({
        ...query,
        offset: 0,
        userId: id,
        creatorId,
      })
    }
  }, [dispatch, id, creatorId, loadData, query])

  return (
    <div className={s.PostList}>
      {loading === LoadingType.Idle && list.length === 0 && (
        <Flex vertical={true} justify="center" align="center">
          <Result icon={<FileOutlined />} title="No post" />
        </Flex>
      )}
      <div className={s.PostListContent}>
        {list.map((item) => (
          <PostCard key={item.post.id} creatorId={creatorId} post={item} />
        ))}
      </div>
      {(loading === LoadingType.More ||
        (loading === LoadingType.Idle && hasMore)) && (
          <Flex
            ref={sentryRef}
            align="center"
            justify="center"
            style={{ minHeight: 40 }}
          >
            <Spin />
          </Flex>
        )}
    </div>
  )
}
